/**
 * TanStack Query bindings for the **deployment-wide** provider-interface read
 * and the trust-anchor upload (ADR-040 §D-1 / §D-10).
 *
 * ``GET /api/admin/providers/interfaces`` answers "every provider interface in
 * this deployment", which is what a handover from an interface name resolves
 * against.
 *
 * # The interface mutations are not here
 *
 * They nest under their provider (``…/{provider_id}/interfaces/{interface_id}``)
 * and live in :file:`use-providers.ts` beside the provider-scoped read they
 * belong to — a mutation needs a ``providerId`` this flat read deliberately does
 * not carry for a non-privileged session. Keeping them apart is what stops a
 * caller reaching for this module's hook and finding a mutation silently wanting
 * a parent it does not have. REQ-PROVIDER-016 is why the routes nest at all: the
 * audit record must name the full ``provider / interface / connection`` path.
 *
 * Server state lives in the query cache and only there — per coding-standards
 * §2.4 the interface list is never copied into Zustand or a long-lived
 * ``useState``, because a stale copy of an interface's ``state`` is a kill
 * switch showing the wrong position.
 *
 * # Reads are all-roles
 *
 * :func:`useProviderInterfaces` mirrors the BFF's read gate so the surface
 * renders for every authenticated role (REQ-UI-165), field-limited by the
 * server. The upload carries no role check of its own: it renders only for
 * `admin` / `support_engineer`, and the BFF is the enforcement point regardless
 * (ADR-009 defence-in-depth — UI hiding is cosmetic).
 *
 * Cross-references:
 *   - ADR-040 §D-1 / §D-10; REQ-PROVIDER-030 (trust anchors are D-10 validated).
 *   - REQ-UI-035 (BFF-only calls); REQ-AUTH-022 (BFF-side RBAC); REQ-UI-165.
 */
import {
  type UseMutationResult,
  type UseQueryResult,
  useMutation,
  useQuery,
} from "@tanstack/react-query";
import { ApiError, apiFetch } from "@/lib/api-client";
import { useSession } from "@/auth/session-context";
import type { Role } from "@/auth/session-types";
import { parseProviderInterfaceList, type ProviderInterfaceView } from "@/providers/provider-model";

/** Roles the BFF lets **read** the provider surface, field-limited (REQ-UI-165). */
export const PROVIDER_READER_ROLES: readonly Role[] = [
  "operator",
  "analyst",
  "support_engineer",
  "admin",
];

/**
 * Roles the BFF lets **mutate** provider configuration (ADR-040 §D-10).
 *
 * REQ-UI-143 requires the mutation controls be **absent** for other roles, not
 * disabled: a disabled control tells an operator the system has a capability
 * they may not use, which on the surface governing what enters the deployment is
 * an invitation to go looking for a way to use it.
 */
export const PROVIDER_MUTATOR_ROLES: readonly Role[] = ["admin", "support_engineer"];

/** TanStack Query key for the deployment-wide interface list. */
export const PROVIDER_INTERFACES_KEY: readonly unknown[] = ["provider-manager", "interfaces"];

const BASE = "/api/admin/providers";

/**
 * Fetch every configured Provider Interface.
 *
 * The role gate mirrors the BFF's so a session carrying no known Meridian role
 * never issues the request. The response is shape-guarded at the trust boundary
 * (:func:`parseProviderInterfaceList`) before it reaches a component — a drifted
 * payload fails loudly rather than rendering a half-populated ingest path.
 *
 * @param enabled Whether to issue the request at all, ANDed with the role gate.
 *   **Not a convenience**: this list is only needed as the *fallback* leg of the
 *   dialog's handover resolution (a provider name is matched first, against a
 *   roster already loaded), so the cost belongs to the one caller that needs it
 *   rather than to every session.
 */
export function useProviderInterfaces(
  enabled = true,
): UseQueryResult<ProviderInterfaceView[], ApiError> {
  const session = useSession();
  return useQuery<ProviderInterfaceView[], ApiError>({
    queryKey: PROVIDER_INTERFACES_KEY,
    enabled: enabled && session.hasAnyRole(PROVIDER_READER_ROLES),
    queryFn: async () => parseProviderInterfaceList(await apiFetch<unknown>(`${BASE}/interfaces`)),
  });
}

/** One certificate the BFF parsed out of the upload, echoed for out-of-band verification. */
export interface InspectedProviderCertificate {
  subject: string;
  issuer: string;
  not_before: string;
  not_after: string;
  fingerprint_sha256: string;
  is_ca: boolean;
}

/** The trust-anchor upload response: the server-chosen path plus what it parsed. */
export interface ProviderTrustAnchorUploadResult {
  /** The server-generated path an operator pastes into ``provider_ca_bundle_ref``. */
  stored_path: string;
  content_sha256: string;
  certificates: InspectedProviderCertificate[];
}

/**
 * Upload a **provider CA bundle** (``POST …/providers/cert-material``) —
 * REQ-PROVIDER-030.
 *
 * ⚠ **One kind of material, and no ``kind`` field** — deliberately unlike the
 * egress sibling, whose surface accepts two. Meridian's own provider-facing
 * server identity is minted at install and is **never uploadable**
 * (REQ-PROVIDER-035): a key routed through nginx, the BFF and application memory
 * cannot afterwards be *proven* uncopied, and the certificate beside it never
 * passes a proving path either. Because every accepted certificate must carry
 * ``CA=true``, a server leaf is refused by the anchor rule itself rather than by
 * a special case.
 *
 * **Validation is enforced here, not merely echoed.** An anchor that is expired
 * or is not a CA is refused at upload, because storing one silently produces an
 * interface that refuses every provider connection with no visible reason. The
 * response echoes subject / issuer / validity / SHA-256 fingerprint so the
 * operator can verify out of band against what the provider's operator told
 * them, rather than trusting the transfer.
 */
export function useUploadProviderTrustAnchor(): UseMutationResult<
  ProviderTrustAnchorUploadResult,
  ApiError,
  File
> {
  return useMutation<ProviderTrustAnchorUploadResult, ApiError, File>({
    mutationFn: async (file) => {
      const form = new FormData();
      form.append("file", file);
      // No Content-Type header: the browser must set the multipart boundary.
      // `apiFetch` leaves a non-string body alone and still injects CSRF.
      return apiFetch<ProviderTrustAnchorUploadResult>(`${BASE}/cert-material`, {
        method: "POST",
        body: form,
      });
    },
  });
}
